import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  buildPlanStarter,
  catalogBySlug,
  MORE_TIME_MINUTES,
  moreTimeRowEntry,
  parseProgramFromPlanId,
  resolveSessionIndexById,
  weightedLastSets,
  type EffortScale,
  type Exercise,
  type MoreTimeItem,
  type MoreTimeMinutes,
  type PlannedExerciseEntry,
} from '@heraion/shared';

import { useProgramModel } from '../Library/programModelSource';
import type { LoggerHostDeps } from './hostDeps';
import { lastLoggedSetsBeyondWindow } from './lastLoggedBeyondWindow';
import type { LoggerMode } from './loggerMode';
import { MoreTimeSheet } from './MoreTimeSheet';
import { hydrateFromPlanStarter } from './planStarterHydration';
import { hasMoreTime, keptFor, type MoreTimeSource } from './moreTimeKept';
import { loadPlanTemplate } from './planTemplateSource';
import type { LoggerDispatch } from './state/loggerDraft';
import type { DraftEntry, LoggerDraft, WeightUnit } from './state/loggerDraftTypes';
import type { MoreTimeStep } from './state/moreTimeReducer';
import type { Theme } from './theme';
import { ensureRecentWorkoutsCached } from './useEntryHistory';
import { useStarterHydrationOptions } from './useStarterHydrationOptions';

// #356: «More time» in the live logger. The session must come from a program
// a model wrote (origin) with slots that keep their model row (rowId), and
// the model must be at hand (network, else the device copy). Otherwise there
// is no door. A slot's step lands on the row started from that slot
// (DraftEntry.planSlotId, moreTimeKept); a step with nothing to land on is
// left out of the list and of the total. The program does not change.

const DEFAULT_MINUTES: MoreTimeMinutes = 20;
const MORE_TIME_MAX: MoreTimeMinutes = 30;

type Args = {
  draft: LoggerDraft;
  dispatch: LoggerDispatch;
  mode: LoggerMode;
  deps: Pick<LoggerHostDeps, 'apiFetch' | 'followedPrograms' | 'maxMeta' | 'profile'>;
  catalog: Exercise[] | null;
  defaultUnit: WeightUnit;
  effortScale: EffortScale;
  resolveName: (slug: string, snapshot?: string) => string;
  t: (k: string, opts?: Record<string, unknown>) => string;
  theme: Theme;
};

/** The program and session behind a live program session, with the model read for it, or null. */
function useMoreTimeSource(ref: string | undefined, deps: Args['deps']): MoreTimeSource | null {
  const [found, setFound] = useState<Omit<MoreTimeSource, 'model'> | null>(null);
  const parsed = parseProgramFromPlanId(ref);
  const followed = parsed ? deps.followedPrograms.find((p) => p.id === parsed.programId) : undefined;
  const { apiFetch } = deps;
  const version = followed?.updatedAt;
  // `followed` is read by its version: a refreshed list with the same program reads nothing again.
  const followedRef = useRef(followed);
  followedRef.current = followed;
  useEffect(() => {
    setFound(null);
    const at = parseProgramFromPlanId(ref);
    if (!ref || !at) return;
    let alive = true;
    void (async () => {
      const mine = followedRef.current;
      const tpl = mine ? null : await loadPlanTemplate(ref, apiFetch);
      const program = mine ?? (tpl?.kind === 'program' ? tpl.program : null);
      if (!program?.origin) return;
      const session = program.sessions[resolveSessionIndexById(program.sessions, at.sessionId, at.sessionIdx)];
      if (!session?.slots.some((s) => s.rowId)) return;
      if (alive) setFound({ program, session, weekIdx: at.weekIdx });
    })();
    return () => {
      alive = false;
    };
  }, [ref, apiFetch, version]);
  const { model } = useProgramModel(found?.program.origin?.modelId, apiFetch);
  return useMemo(() => (found && model ? { ...found, model } : null), [found, model]);
}

export function useMoreTime(args: Args): { onOpen?: () => void; sheet: ReactNode } {
  const { draft, dispatch, mode, deps, catalog, defaultUnit, effortScale, resolveName, t, theme } = args;
  const source = useMoreTimeSource(mode === 'log' ? draft.fromPlanId : undefined, deps);
  const [open, setOpen] = useState(false);
  const [minutes, setMinutes] = useState<MoreTimeMinutes>(DEFAULT_MINUTES);
  // A ref, so two taps in one frame start one build; the state draws it.
  const busyRef = useRef(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  // Bumped by Cancel and by the unmount: a build that ends after either writes nothing.
  const runRef = useRef(0);
  useEffect(
    () => () => {
      runRef.current += 1;
    },
    [],
  );
  const exercises = useMemo(() => catalogBySlug(catalog ?? []), [catalog]);
  const profileRestSec = deps.profile.defaultRestSec;
  const starterOptions = useStarterHydrationOptions({
    locationUnavailable: deps.profile.locationUnavailable,
    equipmentDefault: deps.profile.equipmentDefault,
  });

  // Absent: no door. A model whose rows say nothing to add has none either.
  const available = useMemo(
    () => source !== null && hasMoreTime(source, draft, MORE_TIME_MAX, { effortScale, exercises, profileRestSec }),
    [source, draft, effortScale, exercises, profileRestSec],
  );
  // Every chip's list: a chip whose list is the one before it adds nothing,
  // so it is off, and the preselected one is the highest that is still on.
  const byMinutes = useMemo(
    () =>
      open && source
        ? MORE_TIME_MINUTES.map((m) => keptFor(source, draft, m, { effortScale, exercises, profileRestSec, resolveName, t }))
        : null,
    [open, source, draft, effortScale, exercises, profileRestSec, resolveName, t],
  );
  const spent = useMemo(() => {
    if (!byMinutes) return [];
    const sig = (list: (typeof byMinutes)[number]) =>
      list.map((k) => `${k.item.add}:${k.item.rowId}:${k.item.slotId ?? ''}:${k.item.groupId ?? ''}`).join('|');
    return MORE_TIME_MINUTES.filter((_, i) => i > 0 && sig(byMinutes[i]!) === sig(byMinutes[i - 1]!));
  }, [byMinutes]);
  const live = [...MORE_TIME_MINUTES].reverse().find((m) => !spent.includes(m)) ?? DEFAULT_MINUTES;
  const shown = spent.includes(minutes) ? live : minutes;
  const kept = byMinutes?.[MORE_TIME_MINUTES.indexOf(shown)] ?? [];
  const keptSec = kept.reduce((n, k) => n + k.item.seconds, 0);
  // Never "about 0 min" over rows that are there (#467).
  const totalMin = keptSec > 0 ? Math.max(1, Math.round(keptSec / 60)) : 0;
  // The door went (the model is read again, the program changed): the sheet
  // closes with it, and does not come back on its own when the door does.
  useEffect(() => {
    if (!available && open) setOpen(false);
  }, [available, open]);

  // The rows coming back go through the start's own chain and context: MAX,
  // history and the profile's bodyweight set the load; rounding, saved shapes
  // and the silent machine as the start hydrates (useStarterHydrationOptions).
  const buildRows = async (items: readonly MoreTimeItem[]): Promise<DraftEntry[]> => {
    const planned = items.flatMap((i): PlannedExerciseEntry[] => {
      const e = moreTimeRowEntry(i);
      return e ? [e] : [];
    });
    if (planned.length === 0) return [];
    // Without the catalog a row cannot be built: the sheet says so and stays.
    if (!catalog) throw new Error('more time: no catalog');
    const slugs = planned.map((p) => p.exerciseId);
    const recent = await ensureRecentWorkoutsCached(deps.apiFetch).catch(() => []);
    const lastLogged = await lastLoggedSetsBeyondWindow(slugs, recent, deps.apiFetch).catch(() => new Map());
    const starter = buildPlanStarter({
      entries: planned,
      ctx: {
        currentMaxes: deps.maxMeta,
        bodyweightKg: deps.profile.bodyweightKg ?? null,
        lastLoggedSets: weightedLastSets(lastLogged),
        catalogItems: catalog,
      },
    });
    const opts = await starterOptions(slugs);
    return hydrateFromPlanStarter(starter, defaultUnit, catalog, opts).entries.map((e) => ({ ...e, location: draft.location ?? null }));
  };

  const onConfirm = () => {
    if (busyRef.current) return;
    busyRef.current = true;
    const run = runRef.current;
    setBusy(true);
    setFailed(false);
    void (async () => {
      try {
        const rows = await buildRows(kept.flatMap((k) => (k.item.add === 'row' ? [k.item] : [])));
        if (run !== runRef.current) return;
        let r = 0;
        const steps = kept.flatMap((k): MoreTimeStep[] => {
          if (k.step) return [k.step];
          const entry = rows[r++];
          return entry ? [{ add: 'entry', entry }] : [];
        });
        dispatch({ type: 'add_more_time', steps, defaultUnit });
        setOpen(false);
      } catch {
        // The sheet stays open with its line; Confirm is there again.
        if (run === runRef.current) setFailed(true);
      } finally {
        // A cancelled build was let go by onClose: a newer one may be running.
        if (run === runRef.current) {
          busyRef.current = false;
          setBusy(false);
        }
      }
    })();
  };

  const onClose = () => {
    runRef.current += 1;
    busyRef.current = false;
    setBusy(false);
    setFailed(false);
    setOpen(false);
  };

  return {
    ...(available ? { onOpen: () => setOpen(true) } : {}),
    sheet: available ? (
      <MoreTimeSheet
        visible={open}
        minutes={shown}
        spent={spent}
        onMinutes={(m) => {
          // The build in flight is for the list of the tap: no other chip now.
          if (busyRef.current) return;
          setFailed(false);
          setMinutes(m);
        }}
        rows={kept.map((k) => k.row)}
        totalMin={totalMin}
        busy={busy}
        failed={failed}
        onConfirm={onConfirm}
        onClose={onClose}
        t={t}
        theme={theme}
      />
    ) : null,
  };
}

